import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_ENDPOINTS, buildApiUrl } from '../../config/apiConfig';
import AdminHubPageFrame from '../../components/admin/AdminHubPageFrame';
import AdminFilterBar from '../../components/admin/AdminFilterBar';
import AdminSectionHeader from '../../components/admin/AdminSectionHeader';
import AdminMetricCard from '../../components/admin/AdminMetricCard';
import AdminStatusBadge from '../../components/admin/AdminStatusBadge';
import AdminDataTable from '../../components/admin/AdminDataTable';

function parseRouteStatusEntry(key, count) {
  const match = String(key).match(/^(\d{3})\s+([A-Z]+)\s+(.+)$/);
  if (!match) {
    return { status: '—', method: '—', path: key, count, __key: key };
  }
  return {
    status: match[1],
    method: match[2],
    path: match[3],
    count,
    __key: key,
  };
}

function errorRouteRows(routeStatusMap) {
  if (!routeStatusMap || typeof routeStatusMap !== 'object') return [];
  return Object.entries(routeStatusMap)
    .map(([key, count]) => parseRouteStatusEntry(key, count))
    .filter((row) => {
      const code = Number(row.status);
      return Number.isFinite(code) && code >= 400;
    })
    .sort((a, b) => {
      const countDiff = Number(b.count) - Number(a.count);
      if (countDiff !== 0) return countDiff;
      return String(a.__key).localeCompare(String(b.__key));
    });
}

function statusTone(status) {
  if (status === 'available') return 'ok';
  if (status === 'unavailable') return 'danger';
  return 'warn';
}

function statusLabel(status, t) {
  if (status === 'available') return t('adminObs.statusAvailable');
  if (status === 'unavailable') return t('adminObs.statusUnavailable');
  return t('adminObs.statusUnknown');
}

function fmtNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : '—';
}

export default function AdminObservabilityPage() {
  const { t, isRTL } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [accessError, setAccessError] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    setAccessError(false);
    try {
      const res = await fetch(buildApiUrl(API_ENDPOINTS.ADMIN_OBSERVABILITY), {
        credentials: 'include',
      });
      if (res.status === 401 || res.status === 403) {
        setAccessError(true);
        setData(null);
        return;
      }
      if (!res.ok) throw new Error('bad_status');
      const json = await res.json();
      setData(json);
      setLastUpdated(new Date());
    } catch {
      setFetchError(true);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const metrics = data?.ydim?.metrics;
  const metricsEnabled = !!data?.ydim?.metrics_enabled && metrics?.enabled !== false;

  const errorRouteTableRows = useMemo(
    () => errorRouteRows(metrics?.responses_by_route_status),
    [metrics?.responses_by_route_status]
  );

  const errorRouteColumns = useMemo(
    () => [
      { key: 'status', header: t('adminObs.colStatus') },
      { key: 'method', header: t('adminObs.colMethod') },
      { key: 'path', header: t('adminObs.colPath') },
      { key: 'count', header: t('adminObs.colCount') },
    ],
    [t]
  );

  const crumbs = useMemo(
    () => [
      { label: t('adminHub.navPlatform'), to: '/admin/platform/readiness' },
      { label: t('adminHub.navObservability') },
    ],
    [t]
  );

  const lastUpdatedLabel = lastUpdated
    ? lastUpdated.toLocaleString(isRTL ? 'ar-SA' : 'en-GB', { dateStyle: 'medium', timeStyle: 'short' })
    : '—';

  const serviceBadge = (status) => (
    <AdminStatusBadge tone={statusTone(status)}>{statusLabel(status, t)}</AdminStatusBadge>
  );

  return (
    <AdminHubPageFrame
      titleKey="adminHub.observabilityTitle"
      descKey="adminHub.observabilityDesc"
      breadcrumbItems={crumbs}
    >
      <section className="admin-section-card">
        <AdminFilterBar>
          <button type="button" className="admin-hub-btn" onClick={load} disabled={loading}>
            {loading ? t('adminPage.loading') : t('adminObs.refreshData')}
          </button>
          <span className="admin-hub-kit-metric__hint">
            {t('adminObs.lastUpdated')}: {lastUpdatedLabel}
          </span>
        </AdminFilterBar>

        {accessError ? (
          <div className="admin-state admin-state--error" role="status">
            <p>{t('adminObs.accessDenied')}</p>
          </div>
        ) : null}

        {fetchError ? (
          <div className="admin-state admin-state--error" role="status">
            <p>{t('adminObs.loadError')}</p>
          </div>
        ) : null}

        {!metricsEnabled && data && !fetchError && !accessError ? (
          <div className="admin-state" role="status">
            <p>{t('adminObs.metricsDisabled')}</p>
          </div>
        ) : null}

        {data && !fetchError && !accessError ? (
          <div className="admin-hub-kit-stack">
            <AdminSectionHeader title={t('adminObs.sectionYdim')} />
            <div className="admin-hub-kit-metric-grid">
              <AdminMetricCard
                label={t('adminObs.cardYdimStatus')}
                value={serviceBadge(data.ydim?.status || 'unknown')}
              />
              <AdminMetricCard
                label={t('adminObs.cardRequestsTotal')}
                value={metricsEnabled ? fmtNum(metrics?.requests_total) : '—'}
              />
              <AdminMetricCard
                label={t('adminObs.cardResponseAvg')}
                value={
                  metricsEnabled
                    ? `${fmtNum(metrics?.response_time_ms_avg)} ms`
                    : '—'
                }
              />
              <AdminMetricCard
                label={t('adminObs.cardErrors4xx')}
                value={metricsEnabled ? fmtNum(metrics?.errors_4xx) : '—'}
              />
              <AdminMetricCard
                label={t('adminObs.cardErrors5xx')}
                value={metricsEnabled ? fmtNum(metrics?.errors_5xx) : '—'}
              />
              <AdminMetricCard
                label={t('adminObs.cardSentry')}
                value={
                  metricsEnabled ? (
                    <AdminStatusBadge tone={metrics?.sentry_configured ? 'ok' : 'warn'}>
                      {metrics?.sentry_configured
                        ? t('adminObs.sentryOn')
                        : t('adminObs.sentryOff')}
                    </AdminStatusBadge>
                  ) : (
                    <AdminStatusBadge tone="warn">{t('adminObs.statusUnknown')}</AdminStatusBadge>
                  )
                }
              />
            </div>

            {metricsEnabled ? (
              <>
                <AdminSectionHeader title={t('adminObs.errorsByRouteTitle')} level={3} />
                <AdminDataTable
                  columns={errorRouteColumns}
                  rows={errorRouteTableRows}
                  compact
                  emptyMessage={t('adminObs.errorsByRouteEmpty')}
                />
              </>
            ) : null}

            <AdminSectionHeader title={t('adminObs.sectionServices')} level={3} />
            <div className="admin-hub-kit-metric-grid">
              <AdminMetricCard
                label={t('adminObs.cardPrivacyGateway')}
                value={serviceBadge(data.services?.privacy_gateway?.status)}
                hint={data.services?.privacy_gateway?.checked_url || undefined}
              />
              <AdminMetricCard
                label={t('adminObs.cardLie')}
                value={serviceBadge(data.services?.legal_intelligence_engine?.status)}
                hint={data.services?.legal_intelligence_engine?.checked_url || undefined}
              />
              <AdminMetricCard
                label={t('adminObs.cardIngestion')}
                value={serviceBadge(data.services?.ingestion?.status)}
                hint={data.services?.ingestion?.checked_url || undefined}
              />
            </div>
          </div>
        ) : null}
      </section>
    </AdminHubPageFrame>
  );
}
